import { Form, Formik, useFormikContext } from 'formik'
import React, { FunctionComponent, useEffect } from 'react'
import styled, { keyframes } from 'styled-components'

import { Button } from '../../components/Button'
import { Column, Heading1, Heading3, Link, Notification, Row, Text } from '../../components/layout'
import { PasswordInput, TextInput } from '../../components/layout/form/input'
import { ScrollToError } from '../../components/layout/form/ScrollToError'
import { hasRequiredVersionForFeature, isRunningInWebview } from '../../graphql-client/storedState'
import { ToggleSwitch } from '../../components/Switch'

export interface LoginFormValues {
  username: string
  password: string
  rememberMe: boolean
}

interface Props {
  login: (values: LoginFormValues) => void
  loginFailed: boolean
  usernamePlaceholder: string
  passwordPlaceholder: string
  loginLabel: string
  loading: boolean
}

interface LoginFormContentProps {
  loginFailed: boolean
  loading: boolean
  passwordPlaceholder: string
  usernamePlaceholder: string
  loginLabel: string
}

const LoginFormContent: FunctionComponent<LoginFormContentProps> = ({
  loginFailed,
  loading,
  passwordPlaceholder,
  usernamePlaceholder,
  loginLabel,
}) => {
  const { values, setFieldValue } = useFormikContext<LoginFormValues>()
  return (
    <Form onKeyDown={(e: React.KeyboardEvent) => e.key === 'Enter' && e.preventDefault()}>
      <SetCredentialsFromWebview />
      <LoginFormContainer>
        <LoginRow>
          <Column>
            <TextInput
              placeholder={usernamePlaceholder}
              name={'username'}
              autoComplete='username'
              required
            />
          </Column>
        </LoginRow>
        <LoginRow>
          <Column>
            <PasswordInput placeholder={passwordPlaceholder} name="password" required />
          </Column>
        </LoginRow>
        {isRunningInWebview() && hasRequiredVersionForFeature({ iOSVersion: "1.0.6", AndroidVersion: "1.2.2" }) && (
          <LoginRow>
            <Column>
              <ToggleSwitch
                label="Muista minut"
                checked={values.rememberMe}
                onChange={v => setFieldValue("rememberMe", v)}
              />
            </Column>
          </LoginRow>
        )}
        <LoginRow>
          <LoginRow>
            <Column>
              {loading ? (
                <Button disabled category="regular" submit>
                  <StyledSpinner />
                </Button>
              ) : (
                <Button category="regular" label={loginLabel} submit />
              )}
            </Column>
          </LoginRow>
        </LoginRow>
        <LoginRow>
          <Column>
            {loginFailed && (
              <Notification type="error">
                ! Kirjautuminen epäonnistui. Tarkista tunnus ja salasana.
              </Notification>
            )}
          </Column>
        </LoginRow>
      </LoginFormContainer>

      <ScrollToError />
      {loginFailed && (
        <LoginRow>
          <Column>
            <Link
              target="_blank"
              href="https://ssl.innovoice.fi/auth/request_reset"
              style={{ marginTop: '1rem', textAlign: 'center' }}
            >
              Unohditko salasanasi?{' '}
            </Link>
          </Column>
        </LoginRow>
      )}
    </Form>
  )
}

export const LoginForm: FunctionComponent<Props> = ({
  login,
  loginFailed,
  loading,
  passwordPlaceholder,
  usernamePlaceholder,
  loginLabel,
}) => {
  return (
    <>
      <Row justify="center">
        <Heading1>InnoHinaus</Heading1>
      </Row>
      <Row justify="center">
        <Heading3>Kuljettajan työlista ja kuormakirja</Heading3>
      </Row>
      <Formik
        initialValues={{ username: '', password: '', rememberMe: true }}
        onSubmit={(values: LoginFormValues) => login(values)}
      >
        {() => (
          <LoginFormContent
            loginFailed={loginFailed}
            loading={loading}
            passwordPlaceholder={passwordPlaceholder}
            usernamePlaceholder={usernamePlaceholder}
            loginLabel={loginLabel}
          />
        )}
      </Formik>

      <InfoTextRow>
        <InfoTextColumn>
          <Heading3>Asiakaspalvelu</Heading3>
          <Text>09 4241 2300</Text>
          <Text>asiakaspalvelu@innovoice.fi</Text>
        </InfoTextColumn>
      </InfoTextRow>
    </>
  )
}

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`

export const StyledSpinner = styled.div`
  border: 3px solid #f3f3f3;
  border-top: 3px solid #3498db;
  border-radius: 50%;
  width: 22px;
  height: 22px;
  animation: ${spin} 0.8s linear infinite;
  margin: 0 auto;
`

// This component sets up a global function to receive credentials from the webview environment
const SetCredentialsFromWebview = () => {
  const { setFieldValue } = useFormikContext<LoginFormValues>()
  useEffect(() => {
    if(isRunningInWebview()) {
      ;(window as any).setLoginValues = (username: string, password: string) => {
        setFieldValue('username', username)
        setFieldValue('password', password)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return null
}

const LoginFormContainer = styled.div`
  padding: 1rem;
  margin: auto;
  max-width: 320px;
`

const LoginRow = styled.div`
  display: flex;
  justify-content: center;
  margin: 0 0 1rem 0;
`

const InfoTextRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: 0 2rem;
  width: 100%;
  position: absolute;
  bottom: 2rem;
  left: 0;
  right: 0;
  @media (max-width: ${props => props.theme.screenSize.mobile}) {
    flex-direction: column;
    justify-content: flex-start;
  }
`

interface InfoTextColumnProps {
  align?: string
}

const InfoTextColumn = styled.div<InfoTextColumnProps>`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  text-align: ${props => props.align};
  @media (max-width: ${props => props.theme.screenSize.tablet}) {
    flex-direction: column;
    justify-content: flex-start;
    text-align: left;
  }
`
